"use client";

import { useCallback, useMemo, useRef, useState } from "react";

import type { TokenUsageMeta } from "@/types/chat";

import type { AssessmentUpdateMessage } from "./use-assessment-sync";
import { useAssessmentSync } from "./use-assessment-sync";
import type { RealtimeOrigin } from "./use-realtime-channel";

export interface AssessmentHistoryEntry {
  requestId: string | null;
  status: "created" | "skipped" | "failed";
  score: number | null;
  delta: number | null;
  confidence: number | null;
  triggerSource: string | null;
  latencyMs: number | null;
  errorCode: string | null;
  reason: string | null;
  updatedAt: string;
  origin?: RealtimeOrigin;
  triggerConfig?: {
    minTurns: number;
    turnInterval: number;
    cooldownHours: number;
  } | null;
  tokenUsage?: TokenUsageMeta | null;
}

export interface AssessmentNextHint {
  status: "disabled" | "cooldown" | "waiting_turns" | "ready";
  remainingMs?: number | null;
  remainingTurns?: number | null;
}

export interface AssessmentRhythmState {
  latest?: AssessmentHistoryEntry | null;
  history: AssessmentHistoryEntry[];
  remainingTurns: number | null;
  cooldownRemainingMs: number | null;
  triggerConfig: {
    minTurns: number;
    turnInterval: number;
    cooldownHours: number;
  } | null;
  lastRequestId?: string | null;
  lastTokenUsage?: TokenUsageMeta | null;
  nextAssessmentHint: AssessmentNextHint;
}

export interface UseAssessmentRhythmOptions {
  userId?: string | null;
  enabled?: boolean;
  conversationCount?: number;
  onEvent?: (entry: AssessmentHistoryEntry) => void;
  storageKey?: string;
}

const DEFAULT_STORAGE_KEY = "talkmaster-assessment-rhythm";
const HISTORY_LIMIT = 5;

function loadHistory(storageKey: string): AssessmentHistoryEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const cached = window.localStorage.getItem(storageKey);
    if (!cached) return [];
    const parsed = JSON.parse(cached);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((entry) => {
        const triggerConfig =
          entry?.triggerConfig &&
          typeof entry.triggerConfig === "object" &&
          typeof entry.triggerConfig.minTurns === "number" &&
          typeof entry.triggerConfig.turnInterval === "number"
            ? {
                minTurns: entry.triggerConfig.minTurns,
                turnInterval: entry.triggerConfig.turnInterval,
                cooldownHours:
                  typeof entry.triggerConfig.cooldownHours === "number"
                    ? entry.triggerConfig.cooldownHours
                    : 0,
              }
            : null;

        const rawTokenUsage = entry?.tokenUsage;
        const tokenUsage: TokenUsageMeta | null =
          rawTokenUsage && typeof rawTokenUsage === "object"
            ? {
                prompt:
                  typeof rawTokenUsage.prompt === "number"
                    ? rawTokenUsage.prompt
                    : null,
                completion:
                  typeof rawTokenUsage.completion === "number"
                    ? rawTokenUsage.completion
                    : null,
                total:
                  typeof rawTokenUsage.total === "number"
                    ? rawTokenUsage.total
                    : null,
              }
            : null;

        return {
          requestId:
            typeof entry?.requestId === "string" ? entry.requestId : null,
          status:
            entry?.status === "created" ||
            entry?.status === "skipped" ||
            entry?.status === "failed"
              ? entry.status
              : "created",
          score: typeof entry?.score === "number" ? entry.score : null,
          delta: typeof entry?.delta === "number" ? entry.delta : null,
          confidence:
            typeof entry?.confidence === "number" ? entry.confidence : null,
          triggerSource:
            typeof entry?.triggerSource === "string"
              ? entry.triggerSource
              : null,
          latencyMs:
            typeof entry?.latencyMs === "number" ? entry.latencyMs : null,
          errorCode:
            typeof entry?.errorCode === "string" ? entry.errorCode : null,
          reason: typeof entry?.reason === "string" ? entry.reason : null,
          updatedAt:
            typeof entry?.updatedAt === "string"
              ? entry.updatedAt
              : new Date().toISOString(),
          triggerConfig,
          tokenUsage,
        };
      })
      .slice(0, HISTORY_LIMIT);
  } catch {
    return [];
  }
}

function persistHistory(storageKey: string, history: AssessmentHistoryEntry[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      storageKey,
      JSON.stringify(history.slice(0, HISTORY_LIMIT)),
    );
  } catch {
    // ignore storage errors
  }
}

function computeRemainingTurns(params: {
  conversationCount?: number;
  triggerConfig?: AssessmentRhythmState["triggerConfig"];
}): number | null {
  const { conversationCount, triggerConfig } = params;
  if (!triggerConfig) return null;
  const turns =
    typeof conversationCount === "number" ? conversationCount : null;
  if (turns == null) return null;
  const remainder = turns % triggerConfig.turnInterval;
  const diff = triggerConfig.turnInterval - remainder;
  return diff === triggerConfig.turnInterval ? 0 : diff;
}

function computeCooldownRemainingMs(params: {
  lastEntry?: AssessmentHistoryEntry;
  triggerConfig?: AssessmentRhythmState["triggerConfig"];
}): number | null {
  const { lastEntry, triggerConfig } = params;
  if (!lastEntry || !triggerConfig || triggerConfig.cooldownHours <= 0)
    return null;
  const updatedAt = lastEntry.updatedAt
    ? new Date(lastEntry.updatedAt).getTime()
    : NaN;
  if (Number.isNaN(updatedAt)) return null;
  const cooldownMs = triggerConfig.cooldownHours * 3600_000;
  const remaining = updatedAt + cooldownMs - Date.now();
  return remaining > 0 ? remaining : 0;
}

function computeNextAssessmentHint({
  triggerConfig,
  remainingTurns,
  cooldownRemainingMs,
}: {
  triggerConfig: AssessmentRhythmState["triggerConfig"];
  remainingTurns: number | null;
  cooldownRemainingMs: number | null;
}): AssessmentNextHint {
  if (!triggerConfig) {
    return { status: "disabled" };
  }
  if (cooldownRemainingMs != null && cooldownRemainingMs > 0) {
    return { status: "cooldown", remainingMs: cooldownRemainingMs };
  }
  if (remainingTurns != null && remainingTurns > 0) {
    return { status: "waiting_turns", remainingTurns };
  }
  return { status: "ready", remainingTurns: remainingTurns ?? 0 };
}

export function useAssessmentRhythm({
  userId,
  enabled = true,
  conversationCount,
  onEvent,
  storageKey = DEFAULT_STORAGE_KEY,
}: UseAssessmentRhythmOptions = {}): AssessmentRhythmState {
  const [history, setHistory] = useState<AssessmentHistoryEntry[]>(() =>
    loadHistory(storageKey),
  );
  const [latest, setLatest] = useState<AssessmentHistoryEntry | null>(
    history[0] ?? null,
  );
  const [triggerConfig, setTriggerConfig] =
    useState<AssessmentRhythmState["triggerConfig"]>(null);
  const [lastRequestId, setLastRequestId] = useState<string | null>(
    history[0]?.requestId ?? null,
  );
  const [lastTokenUsage, setLastTokenUsage] = useState<TokenUsageMeta | null>(
    history[0]?.tokenUsage ?? null,
  );

  const processedRequestIdsRef = useRef<Set<string>>(
    new Set(
      history.filter((h) => h.requestId).map((h) => h.requestId as string),
    ),
  );

  const pushHistory = useCallback(
    (entry: AssessmentHistoryEntry) => {
      setHistory((prev) => {
        const next = [
          entry,
          ...prev.filter((item) => item.requestId !== entry.requestId),
        ];
        const trimmed = next.slice(0, HISTORY_LIMIT);
        persistHistory(storageKey, trimmed);
        return trimmed;
      });
    },
    [storageKey],
  );

  const handleUpdate = useCallback(
    (payload: AssessmentUpdateMessage) => {
      if (!payload) return;
      const normalized: AssessmentHistoryEntry = {
        requestId:
          typeof payload.requestId === "string" ? payload.requestId : null,
        status: payload.status ?? "created",
        score: typeof payload.score === "number" ? payload.score : null,
        delta: typeof payload.delta === "number" ? payload.delta : null,
        confidence:
          typeof payload.confidence === "number" ? payload.confidence : null,
        triggerSource:
          typeof payload.triggerSource === "string"
            ? payload.triggerSource
            : null,
        latencyMs:
          typeof payload.latencyMs === "number" ? payload.latencyMs : null,
        errorCode:
          typeof payload.errorCode === "string" ? payload.errorCode : null,
        reason: typeof payload.reason === "string" ? payload.reason : null,
        updatedAt: payload.updatedAt ?? new Date().toISOString(),
      };

      if (
        normalized.requestId &&
        processedRequestIdsRef.current.has(normalized.requestId)
      ) {
        // already handled this request
        return;
      }

      if (normalized.requestId) {
        processedRequestIdsRef.current.add(normalized.requestId);
      }

      setLatest(normalized);
      pushHistory(normalized);
      setLastRequestId(normalized.requestId ?? null);
      setLastTokenUsage(payload.tokenUsage ?? null);
      const normalizedTriggerConfig =
        payload.triggerConfig &&
        typeof payload.triggerConfig.minTurns === "number" &&
        typeof payload.triggerConfig.turnInterval === "number" &&
        typeof payload.triggerConfig.cooldownHours === "number"
          ? {
              minTurns: payload.triggerConfig.minTurns,
              turnInterval: payload.triggerConfig.turnInterval,
              cooldownHours: payload.triggerConfig.cooldownHours,
            }
          : null;
      setTriggerConfig(normalizedTriggerConfig);
      normalized.triggerConfig = normalizedTriggerConfig;
      normalized.origin = payload.origin;
      normalized.tokenUsage = payload.tokenUsage ?? null;
      onEvent?.(normalized);
    },
    [onEvent, pushHistory],
  );

  useAssessmentSync({
    userId,
    enabled,
    onUpdate: handleUpdate,
  });

  const remainingTurns = useMemo(
    () => computeRemainingTurns({ conversationCount, triggerConfig }),
    [conversationCount, triggerConfig],
  );

  const cooldownRemainingMs = useMemo(
    () =>
      computeCooldownRemainingMs({
        lastEntry: latest ?? history[0],
        triggerConfig,
      }),
    [history, latest, triggerConfig],
  );

  const nextAssessmentHint = useMemo(
    () =>
      computeNextAssessmentHint({
        triggerConfig,
        remainingTurns,
        cooldownRemainingMs,
      }),
    [cooldownRemainingMs, remainingTurns, triggerConfig],
  );

  return {
    latest,
    history,
    remainingTurns,
    cooldownRemainingMs,
    triggerConfig,
    lastRequestId,
    lastTokenUsage,
    nextAssessmentHint,
  };
}
